// @ts-nocheck
import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

/**
 * Pay the outstanding balance on an existing event registration.
 *
 * This calls `rle-create-billing-checkout`, which takes only item keys
 * ({ type: 'registration', id }) and recomputes the amount server side from
 * event_registrations_v2 (total_amount_due - total_amount_paid + refunded).
 * No price is sent from the browser.
 *
 * History: this hook used to call `stripe-event-checkout` with an
 * `amount_cents` taken from the page. That function has never existed on this
 * project (a direct call returns "Requested function was not found"), so every
 * caller failed at the network. The old `amount` argument is intentionally
 * gone — the server is the only place a price is decided.
 */
interface PurchaseEventRegistrationParams {
  /** event_registrations_v2.id */
  registrationId: string;
  success_url?: string;
  cancel_url?: string;
}

const ERROR_MESSAGES: Record<string, string> = {
  NO_BALANCE: 'This registration is already paid in full.',
  FORBIDDEN: 'You are not allowed to pay for this registration.',
  NOT_FOUND: 'That registration could not be found.',
  UNAUTHENTICATED: 'Please log in to complete payment.',
};

export const usePurchaseEventRegistration = () => {
  const { user } = useAuth();
  const [isProcessing, setIsProcessing] = useState(false);

  const purchaseEventRegistration = async (params: PurchaseEventRegistrationParams) => {
    const { registrationId, success_url, cancel_url } = params;

    if (!user) {
      toast.error('Please log in to complete payment');
      return;
    }

    if (!registrationId) {
      console.error('[EVENT-CHECKOUT-HOOK] No registrationId supplied', params);
      toast.error('This payment could not be started — missing registration.');
      return;
    }

    setIsProcessing(true);

    try {
      const baseUrl = window.location.origin;
      const { data, error } = await invokeEdgeFunction('rle-create-billing-checkout', {
        body: {
          items: [{ type: 'registration', id: registrationId }],
          success_url: success_url || `${baseUrl}/event-registration-success`,
          cancel_url: cancel_url || `${baseUrl}/event-registration-cancelled`,
        },
      });

      if (error) {
        const code = error.message?.trim();
        throw new Error(ERROR_MESSAGES[code] ?? error.message);
      }

      const url = data?.data?.url;
      if (!url) {
        toast.error('No checkout URL was returned. Please try again.');
        return;
      }

      window.location.href = url;
    } catch (error: any) {
      console.error('[EVENT-CHECKOUT-HOOK] Payment failed:', error?.message || error);
      toast.error(error?.message || 'Failed to start payment. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    purchaseEventRegistration,
    isProcessing,
  };
};
